import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Calendar, Edit, Trash2, CalendarDays, CalendarRange, CalendarX2, Clock, User, Download, X } from 'lucide-react';
import { useTimezone } from '../../../../../hooks/useTimezone';
import { formatInTimezone, formatTimeOnly, formatDateOnly } from '../../../../../utils/timezoneUtils';
import { useSocket } from '../../../../../hooks/useSocket';
import { useAuth } from '../../../../../context/AuthContext';

// Helper function to get today's date range
const getTodayDateRange = () => {
  const today = new Date();
  
  // Start of day (12:00 AM)
  const startOfDay = new Date(today);
  startOfDay.setHours(0, 0, 0, 0);
  
  // End of day (11:59 PM)
  const endOfDay = new Date(today);
  endOfDay.setHours(23, 59, 59, 999);
  
  return {
    start: startOfDay.toISOString(),
    end: endOfDay.toISOString(),
    displayDate: today
  };
};

// Helper function to get past 7 days date range
const getPast7DaysRange = () => {
  const today = new Date();
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(today.getDate() - 6); // 6 days ago + today = 7 days
  
  // Start of 7 days ago
  const startOfRange = new Date(sevenDaysAgo);
  startOfRange.setHours(0, 0, 0, 0);
  
  // End of today
  const endOfRange = new Date(today);
  endOfRange.setHours(23, 59, 59, 999);
  
  return {
    start: startOfRange.toISOString(),
    end: endOfRange.toISOString(),
    displayDate: today
  };
};

// Helper function to get past month date range
const getPastMonthRange = () => {
  const today = new Date();
  const oneMonthAgo = new Date(today);
  oneMonthAgo.setMonth(today.getMonth() - 1);
  
  // Start of one month ago
  const startOfRange = new Date(oneMonthAgo);
  startOfRange.setHours(0, 0, 0, 0);
  
  // End of today
  const endOfRange = new Date(today);
  endOfRange.setHours(23, 59, 59, 999);
  
  return {
    start: startOfRange.toISOString(),
    end: endOfRange.toISOString(),
    displayDate: today
  };
};

const MyTimeEntriesTab = () => {
  const userTimezone = useTimezone();
  const { currentUser } = useAuth();
  const { socket } = useSocket(currentUser?.id);
  
  // State management
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [employees, setEmployees] = useState({}); // Cache for employee data
  
  // Date range state
  const [currentDateView, setCurrentDateView] = useState(getTodayDateRange());
  const [dateRangeType, setDateRangeType] = useState('today'); // 'today', 'past7days', 'pastmonth', 'custom'
  const [filters, setFilters] = useState({
    start_date: getTodayDateRange().start.split('T')[0],
    end_date: getTodayDateRange().end.split('T')[0]
  });
  
  // Custom date range inputs
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  
  // Modal state
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  
  // Export modal state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportColumns, setExportColumns] = useState({});

  // Custom date range handler
  const handleCustomDateRangeSearch = () => {
    if (!customStartDate || !customEndDate) {
      alert('Please select both start and end dates');
      return;
    }
    
    if (new Date(customStartDate) > new Date(customEndDate)) {
      alert('Start date cannot be after end date');
      return;
    }
    
    // Create date range from custom inputs
    const startOfRange = new Date(customStartDate);
    startOfRange.setHours(0, 0, 0, 0);
    
    const endOfRange = new Date(customEndDate);
    endOfRange.setHours(23, 59, 59, 999);
    
    const newDateRange = {
      start: startOfRange.toISOString(),
      end: endOfRange.toISOString(),
      displayDate: new Date(customEndDate) // Use end date for display
    };
    
    setCurrentDateView(newDateRange);
    setDateRangeType('custom');
    
    updateFilters({
      start_date: newDateRange.start.split('T')[0],
      end_date: newDateRange.end.split('T')[0]
    });
  };

  // Handler for date range buttons
  const handleDateRangeChange = (rangeType) => {
    let newDateRange;
    
    switch (rangeType) {
      case 'today':
        newDateRange = getTodayDateRange();
        break;
      case 'past7days':
        newDateRange = getPast7DaysRange();
        break;
      case 'pastmonth':
        newDateRange = getPastMonthRange();
        break;
      default:
        return;
    }
    
    setCurrentDateView(newDateRange);
    setDateRangeType(rangeType);
    
    updateFilters({
      start_date: newDateRange.start.split('T')[0],
      end_date: newDateRange.end.split('T')[0]
    });
  };

  // Helper function to update filters
  const updateFilters = (newFilters) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters
    }));
  };

  // Define columns for export functionality
  const allColumns = [
    { id: 'id', label: 'ID', type: 'integer' },
    { id: 'employee_id', label: 'Employee ID', type: 'integer' },
    { id: 'employee_name', label: 'Employee Name', type: 'text' },
    { id: 'activity', label: 'Activity', type: 'text' },
    { id: 'business_unit_name', label: 'Business Unit', type: 'text' },
    { id: 'project', label: 'Project', type: 'text' },
    { id: 'hours', label: 'Hours', type: 'integer' },
    { id: 'minutes', label: 'Minutes', type: 'integer' },
    { id: 'is_billable', label: 'Billable', type: 'boolean' },
    { id: 'work_completed', label: 'Work Completed', type: 'text' },
    { id: 'date', label: 'Date', type: 'date' },
    { id: 'status', label: 'Status', type: 'text' },
    { id: 'created_at', label: 'Created At', type: 'datetime' },
    { id: 'updated_at', label: 'Updated At', type: 'datetime' },
  ];

  // Open export modal
  const openExportModal = () => {
    if (entries.length === 0) {
      alert('No data to export');
      return;
    }
    
    // Initialize export columns - all columns selected by default
    const initialExportColumns = {};
    allColumns.forEach(column => {
      initialExportColumns[column.id] = true;
    });
    setExportColumns(initialExportColumns);
    setShowExportModal(true);
  };

  // Export to CSV function with selected columns
  const exportToCSV = (selectedColumns) => {
    // Get selected column headers in the correct order
    const headers = allColumns
      .filter(column => selectedColumns[column.id])
      .map(column => column.label);

    // Convert entries to CSV format
    const csvData = entries.map(entry => {
      return allColumns
        .filter(column => selectedColumns[column.id])
        .map(column => {
          let value = entry[column.id];
          
          // Handle special columns
          if (column.id === 'employee_name') {
            // Convert employee_id to employee name using cached data
            value = getEmployeeDisplayName(entry, employees[entry.employee_id]);
          } else if (column.type === 'datetime' && value) {
            try {
              const utcDate = new Date(value);
              if (!isNaN(utcDate.getTime())) {
                value = formatInTimezone(utcDate, userTimezone, {
                  year: 'numeric', month: '2-digit', day: '2-digit',
                  hour: '2-digit', minute: '2-digit', timeZoneName: 'short'
                });
              }
            } catch (error) {
              // Keep original value if formatting fails
            }
          } else if (column.type === 'date' && value) {
            try {
              const dateStr = value.includes('T') ? value.split('T')[0] : value;
              const [year, month, day] = dateStr.split('-').map(Number);
              const dateObj = new Date(year, month - 1, day);
              if (!isNaN(dateObj.getTime())) {
                value = dateObj.toLocaleDateString('en-US');
              }
            } catch (error) {
              // Keep original value if formatting fails
            }
          } else if (column.type === 'boolean' && value !== null && value !== undefined) {
            value = value ? 'Yes' : 'No';
          } else if (value === null || value === undefined) {
            value = '';
          }
          
          // Escape CSV special characters
          if (typeof value === 'string') {
            // If the value contains comma, newline, or quotes, wrap in quotes and escape internal quotes
            if (value.includes(',') || value.includes('\n') || value.includes('"')) {
              value = `"${value.replace(/"/g, '""')}"`;
            }
          }
          
          return value;
        });
    });

    // Combine headers and data
    const csvContent = [headers, ...csvData]
      .map(row => row.join(','))
      .join('\n');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      
      // Generate filename with current date and time
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0]; // YYYY-MM-DD
      const timeStr = now.toTimeString().split(' ')[0].replace(/:/g, '-'); // HH-MM-SS
      link.setAttribute('download', `my_time_entries_${dateStr}_${timeStr}.csv`);
      
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Export modal functions
  const handleExportColumnToggle = (columnId) => {
    setExportColumns(prev => ({
      ...prev,
      [columnId]: !prev[columnId]
    }));
  };

  const handleSelectAllExportColumns = () => {
    const allSelected = {};
    allColumns.forEach(column => {
      allSelected[column.id] = true;
    });
    setExportColumns(allSelected);
  };

  const handleDeselectAllExportColumns = () => {
    const allDeselected = {};
    allColumns.forEach(column => {
      allDeselected[column.id] = false;
    });
    setExportColumns(allDeselected);
  };

  const handleExportConfirm = () => {
    const selectedCount = Object.values(exportColumns).filter(Boolean).length;
    if (selectedCount === 0) {
      alert('Please select at least one column to export');
      return;
    }
    
    exportToCSV(exportColumns);
    setShowExportModal(false);
  };

  // Function to fetch employee details
  const fetchEmployeeDetails = useCallback(async (employeeId) => {
    if (employees[employeeId]) {
      return employees[employeeId]; // Return cached data
    }

    try {
      const response = await fetch(`/api/users/${employeeId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const employee = await response.json();
        const employeeData = {
          full_name: employee.full_name,
          username: employee.username
        };
        
        // Cache the employee data
        setEmployees(prev => ({
          ...prev,
          [employeeId]: employeeData
        }));
        
        return employeeData;
      }
    } catch (error) {
      console.error('Error fetching employee details:', error);
    }
    
    return null;
  }, [employees]);

  // Function to get employee display name
  const getEmployeeDisplayName = (entry, employeeData) => {
    if (employeeData?.full_name) {
      return employeeData.full_name;
    }
    if (employeeData?.username) {
      return employeeData.username;
    }
    return `Employee #${entry.employee_id}`;
  };

  // Fetch user's entries
  useEffect(() => {
    const fetchMyEntries = async () => {
      setLoading(true);
      try {
        let queryParams = new URLSearchParams();
        
        if (debouncedSearchTerm) {
          queryParams.append('search', debouncedSearchTerm);
        }
        
        // Add filters to query params
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== '') {
            queryParams.append(key, value);
          }
        });
        
        queryParams.append('limit', '10000');
        
        const response = await fetch(`/api/mytime?${queryParams.toString()}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}` 
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch entries');
        }
        
        const data = await response.json();
        const entriesData = data.entries || data || [];
        
        // Fetch employee details for each unique employee_id
        const uniqueEmployeeIds = [...new Set(entriesData.map(entry => entry.employee_id))];
        const employeePromises = uniqueEmployeeIds.map(id => fetchEmployeeDetails(id));
        await Promise.all(employeePromises);
        
        setEntries(entriesData);
      } catch (error) {
        console.error('Error fetching my entries:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchMyEntries();
  }, [debouncedSearchTerm, filters, fetchEmployeeDetails]);

  // Debounce search term
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);



  // Handle edit button click
  const handleEditClick = (e, entry) => {
    e.stopPropagation();
    setSelectedEntry(entry);
    setShowEditModal(true);
  };

  // Handle delete button click
  const handleDeleteClick = async (e, entryId) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this entry? This cannot be undone.')) {
      try {
        const response = await fetch(`/api/mytime/${entryId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to delete entry');
        }
        
        // Remove deleted entry from state
        setEntries(entries.filter(entry => entry.id !== entryId));
        setSelectedEntry(null);
      } catch (error) {
        console.error('Error deleting entry:', error);
      }
    }
  };


  // Handle save edit
  const handleSaveEdit = async (formData) => {
    try {
      console.log('Sending data:', formData);
      
      // Ensure proper data formatting for submission
      const submissionData = {
        ...formData,
        business_unit_id: parseInt(formData.business_unit_id) || null,
        hours: parseInt(formData.hours) || 0,
        minutes: parseInt(formData.minutes) || 0,
        is_billable: Boolean(formData.is_billable)
      };
      
      const response = await fetch(`/api/mytime/${selectedEntry.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(submissionData)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Server error response:', errorText);
        throw new Error(`Server error: ${response.status}`);
      }
      
      const updatedData = await response.json();
      
      // Update entries state
      setEntries(entries.map(entry => 
        entry.id === updatedData.id ? updatedData : entry
      ));
      
      setShowEditModal(false);
      alert("Entry updated successfully!");
    } catch (error) {
      console.error('Error updating entry:', error);
      alert(`Error updating entry: ${error.message}`);
    }
  };

  // Group entries by date for timeline view
  const groupEntriesByDate = (entries) => {
    const grouped = {};
    entries.forEach(entry => {
      // Always use entry.date first, fall back to created_at date part if needed
      // Ensure we extract just the date part (yyyy-mm-dd) regardless of format
      let dateKey = entry.date;
      if (!dateKey && entry.created_at) {
        dateKey = entry.created_at.split('T')[0];
      }
      // If dateKey contains time info, strip it
      if (dateKey && dateKey.includes('T')) {
        dateKey = dateKey.split('T')[0];
      }
      
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(entry);
    });
    return grouped;
  };

  const groupedEntries = groupEntriesByDate(entries);

  // Helper function to get display text for current date range
  const getDateRangeDisplayText = () => {
    const safeFormatDate = (date, options) => {
      try {
        if (!date) return 'Unknown Date';
        const dateObj = date instanceof Date ? date : new Date(date);
        if (isNaN(dateObj.getTime())) return 'Invalid Date';
        return formatInTimezone(dateObj, userTimezone, options);
      } catch (error) {
        console.warn('Error formatting date range:', date, error);
        return 'Date Error';
      }
    };

    switch (dateRangeType) {
      case 'today':
        return currentDateView.displayDate.toLocaleDateString('en-US', { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });
      case 'past7days':
        const sevenDaysAgo = new Date(currentDateView.start);
        return `${sevenDaysAgo.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${currentDateView.displayDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
      case 'pastmonth':
        const oneMonthAgo = new Date(currentDateView.start);
        return `${oneMonthAgo.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${currentDateView.displayDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
      case 'custom':
        const customStart = new Date(currentDateView.start);
        const customEnd = new Date(currentDateView.end);
        return `${customStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${customEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
      default:
        return currentDateView.displayDate.toLocaleDateString('en-US', { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });
    }
  };

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm p-6 h-full flex flex-col overflow-hidden">
      {/* Header with controls */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-4">
          <div className="flex items-center bg-slate-700 text-slate-200 border border-slate-600 rounded-md p-2">
            <Calendar className="h-5 w-5 mr-2 text-blue-400" />
            <span className="font-medium">
              {getDateRangeDisplayText()}
            </span>
          </div>
          
          <div className="flex space-x-2">
            <button
              onClick={() => handleDateRangeChange('today')}
              className={`rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                dateRangeType === 'today' 
                  ? 'bg-blue-600 text-white hover:bg-blue-700' 
                  : 'bg-slate-700 text-slate-200 border border-slate-600 hover:bg-slate-600'
              }`}
            >
              <div className="flex items-center">
                <CalendarDays className="h-5 w-5 mr-1" />
                <span>Today</span>
              </div>
            </button>
            
            <button
              onClick={() => handleDateRangeChange('past7days')}
              className={`rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                dateRangeType === 'past7days' 
                  ? 'bg-blue-600 text-white hover:bg-blue-700' 
                  : 'bg-slate-700 text-slate-200 border border-slate-600 hover:bg-slate-600'
              }`}
            >
              <div className="flex items-center">
                <CalendarRange className="h-5 w-5 mr-1" />
                <span>Past 7 Days</span>
              </div>
            </button>
            
            <button
              onClick={() => handleDateRangeChange('pastmonth')}
              className={`rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                dateRangeType === 'pastmonth' 
                  ? 'bg-blue-600 text-white hover:bg-blue-700' 
                  : 'bg-slate-700 text-slate-200 border border-slate-600 hover:bg-slate-600'
              }`}
            >
              <div className="flex items-center">
                <CalendarX2 className="h-5 w-5 mr-1" />
                <span>Past Month</span>
              </div>
            </button>
            
            {/* Custom Date Range Selector */}
            <div className="flex items-center space-x-2 bg-slate-700 border border-slate-600 rounded-md p-2">
              <div className="flex items-center space-x-2">
                <label className="text-slate-300 text-sm">From:</label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="bg-slate-600 text-slate-200 border border-slate-500 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <label className="text-slate-300 text-sm">To:</label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="bg-slate-600 text-slate-200 border border-slate-500 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <button
                onClick={handleCustomDateRangeSearch}
                className="bg-blue-600 text-white rounded px-3 py-1 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <div className="flex items-center">
                  <Search className="h-4 w-4 mr-1" />
                  <span>Search</span>
                </div>
              </button>
            </div>
          </div>
          
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={openExportModal}
            className="bg-green-600 text-white border border-green-500 rounded-md py-2 px-4 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 flex items-center space-x-2"
            title="Export data to CSV"
          >
            <Download className="h-5 w-5" />
            <span>Export CSV</span>
          </button>
          <div className="relative">
            <input
              type="text"
              placeholder="Search my entries..."
              className="bg-slate-700 text-slate-200 border border-slate-600 rounded-md py-2 px-4 pl-10 w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-slate-400" />
          </div>
        </div>
      </div>
      
      {/* Content Area */}
      <div className="flex-1 overflow-auto border border-slate-700 rounded-lg">
        <div className="p-4">
          <h2 className="text-xl font-semibold text-slate-200 mb-4 flex items-center">
            <User className="h-6 w-6 mr-2 text-blue-400" />
            My Time Entries
          </h2>
          
          {loading ? (
            <div className="text-center py-8 text-slate-400">Loading your entries...</div>
          ) : entries.length === 0 ? (
            <div className="text-center py-8 text-slate-400">No entries found for the selected date range</div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedEntries)
                .sort(([a], [b]) => new Date(b) - new Date(a))
                .map(([date, dayEntries]) => (
                  <div key={date} className="bg-slate-700 border border-slate-600 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-slate-200 mb-3 border-b border-slate-600 pb-2">
                      {(() => {
                        if (!date) return 'Unknown Date';
                        try {
                          // Always treat the grouping key as a date-only value (yyyy-mm-dd)
                          // Parse as local date to avoid timezone shifts
                          const [year, month, day] = date.split('-').map(Number);
                          const dateObj = new Date(year, month - 1, day); // month is 0-indexed
                          if (isNaN(dateObj.getTime())) return date;
                          return dateObj.toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          });
                        } catch (error) {
                          console.warn('Error formatting date:', date, error);
                          return date;
                        }
                      })()}
                    </h3>
                    
                    <div className="space-y-2">
                      {dayEntries
                        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                        .map((entry) => (
                          <div
                            key={entry.id}
                            className="bg-slate-800 border border-slate-600 rounded p-2 hover:bg-slate-750"
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                {/* Employee and Time Header */}
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center space-x-3">
                                    <User className="h-4 w-4 text-blue-400" />
                                    <span className="text-slate-200 font-medium">
                                      {getEmployeeDisplayName(entry, employees[entry.employee_id])}
                                    </span>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <Clock className="h-4 w-4 text-orange-400" />
                                    <span className="text-slate-200 font-medium">{entry.hours || 0}h {entry.minutes || 0}m</span>
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                      entry.status === 'approved' ? 'bg-green-900 text-green-300' : 
                                      entry.status === 'rejected' ? 'bg-red-900 text-red-300' : 
                                      'bg-yellow-900 text-yellow-300'
                                    }`}>{entry.status || 'pending'}</span>
                                  </div>
                                </div>
                                
                                {/* Compact Info Grid */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                                  <div>
                                    <span className="text-slate-400">Activity:</span>
                                    <span className="text-slate-200 ml-1">{entry.activity || '-'}</span>
                                  </div>
                                  <div>
                                    <span className="text-slate-400">Business Unit:</span>
                                    <span className="text-slate-200 ml-1">{entry.business_unit_name || '-'}</span>
                                  </div>
                                  <div>
                                    <span className="text-slate-400">Project:</span>
                                    <span className="text-slate-200 ml-1">{entry.project || '-'}</span>
                                  </div>
                                  <div>
                                    <span className="text-slate-400">Billable:</span>
                                    <span className={`ml-1 ${entry.is_billable ? 'text-green-300' : 'text-slate-300'}`}>
                                      {entry.is_billable ? 'Yes' : 'No'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="flex items-center space-x-2 flex-shrink-0 ml-4">
                                <button 
                                  onClick={(e) => handleEditClick(e, entry)}
                                  className="p-1 hover:bg-slate-600 rounded"
                                  title="Edit"
                                >
                                  <Edit className="h-4 w-4 text-blue-400" />
                                </button>
                                <button 
                                  onClick={(e) => handleDeleteClick(e, entry.id)}
                                  className="p-1 hover:bg-slate-600 rounded"
                                  title="Delete"
                                >
                                  <Trash2 className="h-4 w-4 text-red-400" />
                                </button>
                              </div>
                            </div>
                            
                            {/* Always show work completed and timestamps */}
                            <div className="mt-1 pt-1 border-t border-slate-600">
                              {entry.work_completed && (
                                <div className="text-xs mb-1">
                                  <span className="text-slate-400">Work Completed: </span>
                                  <span className="text-slate-300">{entry.work_completed}</span>
                                </div>
                              )}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-xs">
                                <div>
                                  <span className="text-slate-400">Created: </span>
                                  <span className="text-slate-300">
                                    {(() => {
                                      try {
                                        if (!entry.created_at) return '-';
                                        const utcDate = new Date(entry.created_at);
                                        if (isNaN(utcDate.getTime())) return entry.created_at;
                                        return formatInTimezone(utcDate, userTimezone, {
                                          year: 'numeric', month: 'short', day: 'numeric',
                                          hour: '2-digit', minute: '2-digit', timeZoneName: 'short'
                                        });
                                      } catch (error) {
                                        return entry.created_at || '-';
                                      }
                                    })()}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-slate-400">Updated: </span>
                                  <span className="text-slate-300">
                                    {(() => {
                                      try {
                                        if (!entry.updated_at) return '-';
                                        const utcDate = new Date(entry.updated_at);
                                        if (isNaN(utcDate.getTime())) return entry.updated_at;
                                        return formatInTimezone(utcDate, userTimezone, {
                                          year: 'numeric', month: 'short', day: 'numeric',
                                          hour: '2-digit', minute: '2-digit', timeZoneName: 'short'
                                        });
                                      } catch (error) {
                                        return entry.updated_at || '-';
                                      }
                                    })()}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Edit Modal */}
      {showEditModal && selectedEntry && (
        <EditEntryModal 
          entry={selectedEntry} 
          onSave={handleSaveEdit}
          onCancel={() => setShowEditModal(false)}
        />
      )}
      
      {/* Export Modal */}
      {showExportModal && (
        <ExportModal 
          exportColumns={exportColumns}
          allColumns={allColumns}
          onToggleColumn={handleExportColumnToggle}
          onSelectAll={handleSelectAllExportColumns}
          onDeselectAll={handleDeselectAllExportColumns}
          onConfirm={handleExportConfirm}
          onCancel={() => setShowExportModal(false)}
          entriesCount={entries.length}
        />
      )}
    </div>
  );
};

// Edit Modal Component (reusing from ClockInEntriesTab)
const EditEntryModal = ({ entry, onSave, onCancel }) => {
  const userTimezone = useTimezone();
  const [formData, setFormData] = useState({
    activity: '',
    business_unit_id: '',
    business_unit_name: '',
    business_unit_identifier: '',
    project: '',
    hours: 0,
    minutes: 0,
    is_billable: false,
    work_completed: '',
    date: '',
    created_at: '',
    updated_at: '',
  });

  const [activities, setActivities] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  // Helper function to convert UTC datetime to local datetime input format
  const formatDateTimeForInput = (utcDateTimeString) => {
    if (!utcDateTimeString) return '';
    try {
      // Parse UTC datetime and convert to local timezone
      const utcDate = new Date(utcDateTimeString);
      if (isNaN(utcDate.getTime())) return '';
      
      // Convert to local timezone and format for datetime-local input
      const localDate = new Date(utcDate.getTime() - (utcDate.getTimezoneOffset() * 60000));
      return localDate.toISOString().slice(0, 16); // YYYY-MM-DDTHH:mm format
    } catch (error) {
      console.warn('Error formatting datetime for input:', utcDateTimeString, error);
      return '';
    }
  };

  // Update form data when entry changes
  useEffect(() => {
    setFormData({
      activity: entry.activity || '',
      business_unit_id: entry.business_unit_id ? String(entry.business_unit_id) : '',
      business_unit_name: entry.business_unit_name || '',
      business_unit_identifier: entry.business_unit_identifier || '',
      project: entry.project || '',
      hours: entry.hours || 0,
      minutes: entry.minutes || 0,
      is_billable: Boolean(entry.is_billable === 1 || entry.is_billable === true),
      work_completed: entry.work_completed || '',
      date: entry.date ? entry.date.split('T')[0] : '', // Extract just the date part (yyyy-MM-dd)
      created_at: formatDateTimeForInput(entry.created_at),
      updated_at: formatDateTimeForInput(entry.updated_at),
    });
  }, [entry]);

  // Fetch dropdown options
  useEffect(() => {
    const fetchDropdownOptions = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const headers = { 'Authorization': `Bearer ${token}` };

        const [activitiesRes, businessUnitsRes, projectsRes] = await Promise.all([
          fetch('/api/activities', { headers }),
          fetch('/api/business-units', { headers }),
          fetch('/api/projects', { headers })
        ]);

        if (activitiesRes.ok) setActivities(await activitiesRes.json());
        if (businessUnitsRes.ok) setBusinessUnits(await businessUnitsRes.json());
        if (projectsRes.ok) setProjects(await projectsRes.json());
      } catch (error) {
        console.error("Error fetching dropdown options:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchDropdownOptions();
  }, []);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'business_unit_id') {
      const selectedUnit = businessUnits.find(unit => unit.id === parseInt(value));
      setFormData(prev => ({
        ...prev,
        business_unit_id: value,
        business_unit_name: selectedUnit ? selectedUnit.name : '',
        business_unit_identifier: selectedUnit ? selectedUnit.identifier : ''
      }));
    } else if (name === 'is_billable') {
      setFormData(prev => ({
        ...prev,
        [name]: value === 'true'
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Convert datetime-local inputs back to UTC for storage
    const submissionData = { ...formData };
    
    if (formData.created_at) {
      try {
        const localDate = new Date(formData.created_at);
        submissionData.created_at = localDate.toISOString();
      } catch (error) {
        console.warn('Error converting created_at to UTC:', error);
      }
    }
    
    if (formData.updated_at) {
      try {
        const localDate = new Date(formData.updated_at);
        submissionData.updated_at = localDate.toISOString();
      } catch (error) {
        console.warn('Error converting updated_at to UTC:', error);
      }
    }
    
    // Ensure proper data types
    submissionData.business_unit_id = parseInt(formData.business_unit_id) || null;
    submissionData.hours = parseInt(formData.hours) || 0;
    submissionData.minutes = parseInt(formData.minutes) || 0;
    submissionData.is_billable = Boolean(formData.is_billable);
    
    onSave(submissionData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
      <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-semibold text-slate-200 mb-4">Edit My Time Entry #{entry.id}</h3>
        
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Read-only fields */}
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">ID</label>
              <input 
                type="text" 
                value={entry.id} 
                disabled
                className="bg-slate-700 text-slate-300 border border-slate-600 rounded w-full py-2 px-3 disabled:opacity-70"
              />
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Status</label>
              <input 
                type="text" 
                value={entry.status || 'pending'} 
                disabled
                className="bg-slate-700 text-slate-300 border border-slate-600 rounded w-full py-2 px-3 disabled:opacity-70"
              />
            </div>
            
            {/* Editable fields */}
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Activity</label>
              {loading ? (
                <div className="bg-slate-700 text-slate-400 border border-slate-600 rounded w-full py-2 px-3">
                  Loading options...
                </div>
              ) : (
                <select
                  name="activity"
                  value={formData.activity}
                  onChange={handleChange}
                  className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select an activity</option>
                  {activities.filter(activity => activity.is_active).map(activity => (
                    <option key={activity.id} value={activity.name}>
                      {activity.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Business Unit</label>
              {loading ? (
                <div className="bg-slate-700 text-slate-400 border border-slate-600 rounded w-full py-2 px-3">
                  Loading options...
                </div>
              ) : (
                <select
                  name="business_unit_id"
                  value={formData.business_unit_id}
                  onChange={handleChange}
                  className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a business unit</option>
                  {businessUnits.filter(unit => unit.is_active).map(unit => (
                    <option key={unit.id} value={unit.id}>
                      {unit.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Date</label>
              <input 
                type="date" 
                name="date"
                value={formData.date} 
                onChange={handleChange}
                className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Created At ({userTimezone === 'America/Chicago' ? 'CST' : userTimezone})</label>
              <input 
                type="datetime-local" 
                name="created_at"
                value={formData.created_at} 
                onChange={handleChange}
                className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Updated At ({userTimezone === 'America/Chicago' ? 'CST' : userTimezone})</label>
              <input 
                type="datetime-local" 
                name="updated_at"
                value={formData.updated_at} 
                onChange={handleChange}
                className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Project</label>
              {loading ? (
                <div className="bg-slate-700 text-slate-400 border border-slate-600 rounded w-full py-2 px-3">
                  Loading options...
                </div>
              ) : (
                <select
                  name="project"
                  value={formData.project || ""}
                  onChange={handleChange}
                  className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a project</option>
                  {projects.filter(project => project.is_active).map(project => (
                    <option key={project.id} value={project.name}>
                      {project.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Hours</label>
              <input 
                type="number" 
                name="hours"
                min="0"
                max="24"
                value={formData.hours} 
                onChange={handleChange}
                className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Minutes</label>
              <input 
                type="number" 
                name="minutes"
                min="0"
                max="59"
                value={formData.minutes} 
                onChange={handleChange}
                className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Billable</label>
              <select
                name="is_billable"
                value={formData.is_billable.toString()}
                onChange={handleChange}
                className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="false">No</option>
                <option value="true">Yes</option>
              </select>
            </div>
            
            
            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-400 mb-1">Work Completed</label>
              <textarea 
                name="work_completed"
                value={formData.work_completed} 
                onChange={handleChange}
                rows="3"
                className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              ></textarea>
            </div>
          </div>
          
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={onCancel}
              className="bg-slate-700 text-slate-300 px-4 py-2 rounded hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-400"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Export Modal Component
const ExportModal = ({ 
  exportColumns, 
  allColumns, 
  onToggleColumn, 
  onSelectAll, 
  onDeselectAll, 
  onConfirm, 
  onCancel, 
  entriesCount 
}) => {
  const selectedCount = Object.values(exportColumns).filter(Boolean).length;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
      <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-slate-200">Export to CSV</h3>
          <button
            onClick={onCancel}
            className="text-slate-400 hover:text-slate-200"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <div className="mb-4">
          <p className="text-slate-300 mb-2">
            Select which columns to include in your CSV export ({entriesCount} entries)
          </p>
          <p className="text-sm text-slate-400">
            {selectedCount} of {allColumns.length} columns selected
          </p>
        </div>
        
        {/* Select All / Deselect All buttons */}
        <div className="flex space-x-3 mb-4">
          <button
            onClick={onSelectAll}
            className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Select All
          </button>
          <button
            onClick={onDeselectAll}
            className="bg-slate-600 text-slate-200 px-4 py-2 rounded text-sm hover:bg-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-400"
          >
            Deselect All
          </button>
        </div>
        
        {/* Column selection grid */}
        <div className="mb-6 max-h-96 overflow-y-auto border border-slate-600 rounded-lg p-4">
          <div className="grid grid-cols-2 gap-2">
            {allColumns.map((column) => (
              <label key={column.id} className="flex items-center space-x-2 text-slate-300 p-2 hover:bg-slate-700 rounded">
                <input
                  type="checkbox"
                  checked={exportColumns[column.id] || false}
                  onChange={() => onToggleColumn(column.id)}
                  className="rounded text-blue-500 focus:ring-blue-500 bg-slate-600 border-slate-500"
                />
                <span className="text-sm">{column.label}</span>
                <span className="text-xs text-slate-400">({column.id})</span>
              </label>
            ))}
          </div>
        </div>
        
        {/* Action buttons */}
        <div className="flex justify-end space-x-3">
          <button
            onClick={onCancel}
            className="bg-slate-700 text-slate-300 px-4 py-2 rounded hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-400"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={selectedCount === 0}
            className={`px-4 py-2 rounded focus:outline-none focus:ring-2 ${
              selectedCount === 0
                ? 'bg-slate-600 text-slate-400 cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Download className="h-4 w-4" />
              <span>Export CSV ({selectedCount} columns)</span>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default MyTimeEntriesTab;
